import React, { useEffect } from 'react';
import styled from '@mui/material/styles/styled';
import Typography from '@mui/material/Typography';
import { Box, Stack } from '@mui/material';
import PropTypes from 'prop-types';
import useWindowSize from '../../hooks/useWindowSize';

const StyledParagraph = styled(Typography, {
  shouldForwardProp: (prop) => !['lineClamp', 'expanded'].includes(prop),
})(({ lineClamp, expanded }) => {
  if (!expanded) {
    return {
      display: '-webkit-box',
      WebkitBoxOrient: 'vertical',
      WebkitLineClamp: lineClamp,
      overflow: 'hidden',
      marginBottom: 0,
    };
  }
  return {
    whiteSpace: 'pre-line',
  };
});

const SeeMoreButton = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  cursor: 'pointer',
  padding: `0 8px`,
  '&:hover': {
    color: theme.palette.primary.main,
    textDecoration: 'underline',
  },
}));

function StyledParagraphTypography(props) {
  const { showSeeMore = false, message = '', ...rest } = props;
  const paragraphRef = React.useRef(null);
  const [showReadMore, setShowReadMore] = React.useState(false);
  const [expanded, setExpanded] = React.useState(false);
  const screenSize = useWindowSize();

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (paragraphRef.current && showSeeMore) {
        const { scrollHeight, scrollWidth, clientHeight, clientWidth } =
          paragraphRef.current;

        if (scrollHeight > clientHeight || scrollWidth > clientWidth) {
          setShowReadMore(true);
        } else {
          setShowReadMore(false);
        }
      }
    }, 50);
    return () => clearTimeout(timeout);
  }, [showSeeMore, screenSize, message]);

  const toggleReadMore = () => {
    setExpanded(!expanded);
  };

  return (
    <Stack>
      <StyledParagraph
        {...rest}
        component="div"
        expanded={expanded}
        ref={paragraphRef}
      />
      {showReadMore && (
        <Box alignSelf="end" pr={1}>
          <SeeMoreButton
            variant="caption"
            component="button"
            onClick={toggleReadMore}>
            {expanded ? 'see less' : '...see more'}
          </SeeMoreButton>
        </Box>
      )}
    </Stack>
  );
}

export default StyledParagraphTypography;
